import {
  Box,
  Divider,
  Flex,
  HStack,
  Link,
  Skeleton,
  Spacer,
  VStack,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { date as commonDate } from '@finley-technologies/common';
import { Bank } from '@finley-technologies/finley-types';

import {
  InvoiceComponentGQLType,
  InvoiceFragmentGQLType,
  InvoiceItemGQLType,
  useGetCreditFacilityMetricsQuery,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { DownloadInvoicePreviewButton } from './create/DownloadInvoicePreviewButton';
import {
  getBankAccountDisplay,
  getInterestInvoiceItemLabel,
  TAccountTypeToLabelMap,
} from './utils';
import {
  Typography,
  TypographyVariant,
} from 'packages/common-frontend/components/Typography';
import {
  truncateUuid,
  dollarNumberWithCommas,
  formatCurrencyAmount,
} from 'packages/common-frontend/utils';
import { useGetTAccountsQuery } from 'packages/statements/requests/ledger.gql.generated';

type PreviewInvoiceItem = Omit<
  InvoiceItemGQLType,
  'id' | 'invoiceId' | 'created_at' | 'updated_at' | 'accountingAccount'
>;

type PreviewInvoiceComponent = Omit<
  InvoiceComponentGQLType,
  'id' | 'invoiceId' | 'created_at' | 'updated_at'
>;

type PreviewInvoice = Omit<
  InvoiceFragmentGQLType,
  | 'created_at'
  | 'updated_at'
  | 'id'
  | 'invoiceItems'
  | 'paymentDestinationOrganization'
  | 'invoiceComponentsWithInvoiceItems'
> & {
  invoiceComponentsWithInvoiceItems: {
    invoiceComponent: PreviewInvoiceComponent;
    invoiceItems: PreviewInvoiceItem[];
  }[];
};

export const InvoiceDetailInformationRow = ({
  label,
  labelFontWeight = 'semibold',
  value,
  valueTypographyVariant = 'base-muted',
  valueTextAlign = 'right',
  valueOnClick,
  isSubRow = false,
}: {
  label: string;
  labelFontWeight?: 'normal' | 'semibold';
  value?: string;
  valueTextAlign?: 'left' | 'right';
  valueTypographyVariant?: TypographyVariant;
  valueOnClick?: () => void;
  isSubRow?: boolean; // used to indent the row if it's a sub-level row
}) => {
  return (
    <Flex
      alignItems="top"
      justifyContent="space-between"
      ml={isSubRow ? '24px' : undefined}
      my="8px"
    >
      <Typography fontWeight={labelFontWeight}>{label}</Typography>
      <Box
        onClick={valueOnClick}
        cursor={valueOnClick ? 'pointer' : 'default'}
        _hover={valueOnClick ? { textDecoration: 'underline' } : undefined}
        textAlign={valueTextAlign}
      >
        <Typography variant={valueTypographyVariant}>{value ?? '-'}</Typography>
      </Box>
    </Flex>
  );
};

export const InvoiceItems = ({
  invoice,
  invoiceComponentIndex,
}: {
  invoice: InvoiceFragmentGQLType | PreviewInvoice;
  invoiceComponentIndex: number;
}) => {
  const { invoiceComponentsWithInvoiceItems, creditFacilityId } = invoice;
  const invoiceItems =
    invoiceComponentsWithInvoiceItems[invoiceComponentIndex].invoiceItems;

  const { data: tAccountsData } = useGetTAccountsQuery({
    variables: {
      creditFacilityId,
    },
  });

  const groupedInvoiceItems = useMemo(() => {
    if (tAccountsData) {
      const accruedInterestInvoiceItems: (
        | PreviewInvoiceItem
        | InvoiceItemGQLType
      )[] = [];
      const otherInvoiceItems: (PreviewInvoiceItem | InvoiceItemGQLType)[] = [];

      invoiceItems.forEach((invoiceItem) => {
        const tAccountDetails = tAccountsData.getTAccounts.find(
          (tAccount) => tAccount.id === invoiceItem.accountingAccountId,
        );
        if (tAccountDetails?.tAccount === 'accruedInterest') {
          accruedInterestInvoiceItems.push(invoiceItem);
        } else {
          otherInvoiceItems.push(invoiceItem);
        }
      });

      return { accruedInterestInvoiceItems, otherInvoiceItems };
    }

    return null;
  }, [invoiceItems, tAccountsData]);

  if (groupedInvoiceItems === null || !tAccountsData) {
    return <Skeleton width="full" h="400px" />;
  }

  return (
    <VStack width="full" gap="1rem">
      <HStack justifyContent="space-between" w="100%">
        <Typography fontWeight="semibold">Total</Typography>
        <Typography>{dollarNumberWithCommas(invoice.totalAmount)}</Typography>
      </HStack>
      <Divider />

      {groupedInvoiceItems.otherInvoiceItems.map((invoiceItem, idx) => {
        const tAccountDetails = tAccountsData.getTAccounts.find(
          (tAccount) => tAccount.id === invoiceItem.accountingAccountId,
        );
        if (!tAccountDetails) {
          return <></>;
        }

        const key = `other-${idx}-${invoiceItem.accountingAccountId}`;
        return (
          <HStack key={key} justifyContent="space-between" w="100%">
            <Typography variant="base-muted">
              {TAccountTypeToLabelMap[tAccountDetails.tAccount] ??
                _.startCase(tAccountDetails.tAccount)}
            </Typography>
            <Typography>
              {dollarNumberWithCommas(invoiceItem.amount)}
            </Typography>
          </HStack>
        );
      })}

      {groupedInvoiceItems.accruedInterestInvoiceItems.length > 0 && (
        <>
          <HStack w="100%">
            <Typography variant="base-muted">Interest</Typography>
            <Spacer />
          </HStack>
          {groupedInvoiceItems.accruedInterestInvoiceItems.map(
            (accruedInterestInvoiceItem, idx) => {
              const key = `interest-${idx}-${accruedInterestInvoiceItem.accountingAccountId}`;
              return (
                <HStack key={key} w="100%">
                  <Box width="2em" />
                  <Typography variant="base-muted">
                    {getInterestInvoiceItemLabel({
                      invoiceItem: accruedInterestInvoiceItem,
                      invoiceLoanId: invoice.finleyLoanId,
                    })}
                  </Typography>
                  <Spacer />
                  <Typography>
                    {dollarNumberWithCommas(accruedInterestInvoiceItem.amount)}
                  </Typography>
                </HStack>
              );
            },
          )}
        </>
      )}
    </VStack>
  );
};

export const InvoiceDetailInformation = ({
  invoice,
  isPreview = false,
}: {
  invoice: InvoiceFragmentGQLType | PreviewInvoice;
  isPreview?: boolean;
}) => {
  const {
    invoiceType,
    creditFacility,
    loan,
    destinationBankAccount,
    paymentSourceOrganization,
    invoiceComponentsWithInvoiceItems,
  } = invoice;

  const { data: metricsDataRequest } = useGetCreditFacilityMetricsQuery({
    skip: !loan?.facility,
    variables: {
      creditFacilityId: creditFacility.id,
      postingDate: commonDate.currentDateStr(),
    },
    fetchPolicy: 'no-cache',
  });

  const metrics = metricsDataRequest?.metrics;

  return (
    <Box>
      {isPreview && (
        <DownloadInvoicePreviewButton creditFacilityId={creditFacility.id} />
      )}

      <VStack align="flex-start" gap="2rem">
        {/* Payment Details */}
        <Divider />
        <Typography variant="section-subtitle" trim>
          Payment Details
        </Typography>
        <VStack w="100%" gap="1rem">
          <HStack justifyContent="space-between" w="100%">
            <Typography fontWeight="medium">Invoice Type</Typography>
            <Typography variant="base-muted">
              {_.startCase(invoiceType)}
            </Typography>
          </HStack>
          <HStack justifyContent="space-between" w="100%">
            <Typography fontWeight="medium">Due Date</Typography>
            <Typography variant="base-muted">
              {commonDate.fullDateStrToHumanDateString(invoice.dueDate)}
            </Typography>
          </HStack>
          {invoice.status === 'paid' && invoice.paymentDate && (
            <HStack justifyContent="space-between" w="100%">
              <Typography fontWeight="medium">Payment Date</Typography>
              <Typography variant="base-muted">
                {commonDate.fullDateStrToHumanDateString(invoice.paymentDate)}
              </Typography>
            </HStack>
          )}
          {loan && (
            <>
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">Loan Name</Typography>
                <Typography variant="base-muted">{loan.name || '-'}</Typography>
              </HStack>

              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">Loan ID</Typography>
                <Flex
                  gap="0.5rem"
                  justifyContent="flex-end"
                  w="50%"
                  whiteSpace="wrap"
                >
                  <Link as={RouterLink} to={`/loans/${loan.id}`}>
                    <Typography
                      colorScheme="blue"
                      fontWeight="medium"
                      variant="base-muted"
                    >
                      {truncateUuid(loan.id)}
                    </Typography>
                  </Link>
                  {loan.externalLoanId && (
                    <Typography variant="base-muted">
                      {`(${loan.externalLoanId})`}
                    </Typography>
                  )}
                </Flex>
              </HStack>
            </>
          )}
          <HStack align="top" w="100%" gap="1em">
            <Typography fontWeight="medium">
              Line Items for{' '}
              {commonDate.fullDateStrToHumanDateString(
                invoice.accrualStartDate,
              )}{' '}
              -{' '}
              {commonDate.fullDateStrToHumanDateString(invoice.accrualEndDate)}
            </Typography>
            <Spacer />
            {invoiceComponentsWithInvoiceItems.map(
              (_invoiceComponentWithInvoiceItems, invoiceComponentIndex) => {
                const key = invoiceComponentIndex;
                return (
                  <InvoiceItems
                    key={key}
                    invoice={invoice}
                    invoiceComponentIndex={invoiceComponentIndex}
                  />
                );
              },
            )}
          </HStack>
        </VStack>

        {/* Facility Details */}
        <Divider />
        <Typography variant="section-subtitle" trim>
          Facility Details
        </Typography>
        <VStack w="100%" gap="1rem">
          <HStack justifyContent="space-between" w="100%">
            <Typography fontWeight="medium">Name</Typography>
            <Typography variant="base-muted">
              {_.startCase(creditFacility.name)}
            </Typography>
          </HStack>
          <HStack justifyContent="space-between" w="100%">
            <Typography fontWeight="medium">External Facility ID</Typography>
            <Typography variant="base-muted">
              {metrics?.externalFacilityId ?? '-'}
            </Typography>
          </HStack>
          <HStack justifyContent="space-between" w="100%">
            <Typography fontWeight="medium">Borrower</Typography>
            <Typography variant="base-muted">
              {_.startCase(paymentSourceOrganization.name)}
            </Typography>
          </HStack>
          {loan && (
            <HStack justifyContent="space-between" w="100%">
              <Typography fontWeight="medium">Global Commitment</Typography>
              <Typography variant="base-muted">
                {formatCurrencyAmount(
                  loan.facility.commitmentAmount,
                  Bank.Currency.USD,
                )}
              </Typography>
            </HStack>
          )}
          <HStack justifyContent="space-between" w="100%">
            <Typography fontWeight="medium">
              Global Outstanding Balance
            </Typography>
            {metrics ? (
              <Typography variant="base-muted">
                {formatCurrencyAmount(
                  metrics.outstandingPrincipalBalance,
                  Bank.Currency.USD,
                )}
              </Typography>
            ) : (
              <Skeleton w="10rem" h="1rem" />
            )}
          </HStack>
        </VStack>

        {/* Payment Instructions */}
        <Divider />
        <Typography variant="section-subtitle" trim>
          Payment Instructions
        </Typography>
        <VStack w="100%" gap="1rem">
          <HStack justifyContent="space-between" w="100%">
            <Typography fontWeight="medium">
              Destination Bank Account
            </Typography>
            <Typography variant="base-muted">
              {destinationBankAccount
                ? getBankAccountDisplay({
                    institutionName: destinationBankAccount.institutionName,
                    accountName: destinationBankAccount.name,
                    accountNumber: destinationBankAccount.accountNumber,
                  })
                : '-'}
            </Typography>
          </HStack>
        </VStack>
      </VStack>
    </Box>
  );
};
